/**
 * 十二种可分离混合模式（M1-11）。
 *
 * 公式逐条对照 **CSS Compositing and Blending Level 1 §5.1**（Photoshop 的
 * 可分离模式与该规范一致）。每种模式在这里只写**一次**——
 * WGSL 与 GLSL 由 `generate.ts` 从同一份公式渲染，见 dialect.ts 的说明。
 *
 * 「可分离」的意思是各颜色通道独立计算，与相邻通道无关。这也是这十二种能用
 * 一个 `vec3` 表达式写完的原因；色相/饱和度/颜色/明度那四种不可分离，
 * 需要跨通道运算，归 M1-12。
 *
 * 除法一律要防 NaN：GPU 上 `0.0/0.0` 是 NaN，而 NaN 会穿过 min/max 和选择
 * 传播到最终像素，表现为随机的黑点或白点。写法上先把分母夹到安全范围，
 * 再用 select 覆盖边界情形——两步都不能省。
 */
import type { ShaderDialect } from './dialect';
import { formatFloat } from './dialect';

/**
 * 可分离混合模式的名字。前 12 种顺序与 CSS 规范的列举顺序一致；
 * 后 9 种是 Photoshop 独有、CSS 规范未收录的模式，追加在末尾——
 * 序号一旦排定就是着色器与 CPU 之间的约定（见 generate.ts 的同一条约束），
 * 不能插队。"溶解"的逐通道颜色公式跟 normal 完全一样（见下方 dissolve
 * 条目），真正的颗粒抖动效果只在 CPU 合成桥实现，GPU 路径下会呈现成
 * 普通的 normal 混合（颜色正确，没有抖动纹理）——这是本次新增混合模式里
 * 唯一一处 GPU/CPU 结果不完全一致的地方，因为抖动需要给着色器新增
 * 像素坐标/随机种子这类跟颜色公式无关的 uniform，改动量明显大于其余
 * 纯公式模式，而"溶解"在 Photoshop 里也是最少用到的模式之一。
 */
export const SEPARABLE_BLEND_MODES = [
  'normal',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'colorDodge',
  'colorBurn',
  'hardLight',
  'softLight',
  'difference',
  'exclusion',
  'dissolve',
  'linearBurn',
  'linearDodge',
  'vividLight',
  'linearLight',
  'pinLight',
  'hardMix',
  'subtract',
  'divide',
] as const;

export type SeparableBlendMode = (typeof SEPARABLE_BLEND_MODES)[number];

/**
 * 一条公式：给定方言，产出函数体。
 *
 * 函数体里可用的名字只有参数 `cb`（背景）与 `cs`（源），
 * 以及本函数体内用 `dialect.local` 声明的局部量。
 */
export type BlendFormula = (dialect: ShaderDialect) => string;

/** 除法分母的下限。取 1e-6 而非更小，是为了在半精度下也不下溢成 0。 */
const EPSILON = 1e-6;

/** 生成 `return <expr>;`，顺带统一缩进风格。 */
function ret(expr: string): string {
  return `return ${expr};`;
}

const one = (d: ShaderDialect): string => d.splat(1);
const zero = (d: ShaderDialect): string => d.splat(0);

/** screen 的表达式。overlay/hardLight 复用它，避免第二次写同一个公式。 */
function screenExpr(cb: string, cs: string): string {
  return `${cb} + ${cs} - ${cb} * ${cs}`;
}

/**
 * hardLight(cb, cs)：cs ≤ 0.5 时对 2·cs 做 multiply，否则对 2·cs−1 做 screen。
 *
 * overlay 就是把两个参数对调后的 hardLight（CSS 规范原话），所以这里做成
 * 可复用的表达式生成器，overlay 直接换参数调用——这正是"不手写重复"的具体落实。
 */
function hardLightExpr(d: ShaderDialect, cb: string, cs: string): string {
  const doubled = `(${formatFloat(2)} * ${cs})`;
  const multiply = `${cb} * ${doubled}`;
  const screened = screenExpr(cb, `(${doubled} - ${one(d)})`);
  return d.select(`(${screened})`, `(${multiply})`, d.lessEqual(cs, d.splat(0.5)));
}

/**
 * colorDodge(cb, cs) 的表达式（不含 return）：cb=0 → 0；cs=1 → 1；
 * 否则 min(1, cb/(1−cs))。两个边界都必须显式处理：只靠
 * `min(1, cb/(1-cs))` 的话，cb=0 且 cs=1 时是 0/0=NaN——不是"结果差一点"，
 * 是整块像素坏掉。写成纯表达式（不用 `d.local` declare 局部变量）是因为
 * vividLight 要把它当子表达式嵌两次，局部变量名会撞车。
 */
function colorDodgeExpr(d: ShaderDialect, cb: string, cs: string): string {
  const denom = `max(${one(d)} - (${cs}), ${d.splat(EPSILON)})`;
  const dodge = `min(${one(d)}, (${cb}) / ${denom})`;
  // 先按 cs=1 选 1，再让 cb=0 的分支覆盖它——顺序反了 cb=0,cs=1 会得到 1
  const bright = d.select(dodge, one(d), d.greaterEqual(cs, one(d)));
  return d.select(bright, zero(d), d.lessEqual(cb, zero(d)));
}

/** colorBurn(cb, cs) 的表达式：cb=1 → 1；cs=0 → 0；否则 1 − min(1, (1−cb)/cs)。 */
function colorBurnExpr(d: ShaderDialect, cb: string, cs: string): string {
  const denom = `max(${cs}, ${d.splat(EPSILON)})`;
  const burn = `(${one(d)} - min(${one(d)}, (${one(d)} - (${cb})) / ${denom}))`;
  const dark = d.select(burn, zero(d), d.lessEqual(cs, zero(d)));
  return d.select(dark, one(d), d.greaterEqual(cb, one(d)));
}

export const BLEND_FORMULAS: Readonly<Record<SeparableBlendMode, BlendFormula>> = {
  normal: () => ret('cs'),

  // "溶解"的逐通道颜色公式跟 normal 完全一样——抖动作用在覆盖度上，
  // 不是颜色上，见上面 SEPARABLE_BLEND_MODES 的说明
  dissolve: () => ret('cs'),

  multiply: () => ret('cb * cs'),

  screen: () => ret(screenExpr('cb', 'cs')),

  // 规范：overlay(cb, cs) = hardLight(cs, cb)。参数对调，不是另一个公式
  overlay: (d) => ret(hardLightExpr(d, 'cs', 'cb')),

  darken: () => ret('min(cb, cs)'),

  lighten: () => ret('max(cb, cs)'),

  colorDodge: (d) => ret(colorDodgeExpr(d, 'cb', 'cs')),

  colorBurn: (d) => ret(colorBurnExpr(d, 'cb', 'cs')),

  hardLight: (d) => ret(hardLightExpr(d, 'cb', 'cs')),

  /**
   * softLight。规范里唯一带辅助函数 D(cb) 的一个：
   *
   * ```
   * D(cb) = cb ≤ 0.25 ? ((16·cb − 12)·cb + 4)·cb : sqrt(cb)
   * B = cs ≤ 0.5 ? cb − (1 − 2·cs)·cb·(1 − cb)
   *              : cb + (2·cs − 1)·(D(cb) − cb)
   * ```
   */
  softLight: (d) => {
    const poly = `((${formatFloat(16)} * cb - ${formatFloat(12)}) * cb + ${formatFloat(4)}) * cb`;
    return [
      d.local('dcb', d.select('sqrt(cb)', `(${poly})`, d.lessEqual('cb', d.splat(0.25)))),
      d.local('darker', `cb - (${one(d)} - ${formatFloat(2)} * cs) * cb * (${one(d)} - cb)`),
      d.local('lighter', `cb + (${formatFloat(2)} * cs - ${one(d)}) * (dcb - cb)`),
      ret(d.select('lighter', 'darker', d.lessEqual('cs', d.splat(0.5)))),
    ].join('\n');
  },

  difference: () => ret('abs(cb - cs)'),

  exclusion: () => ret(`cb + cs - ${formatFloat(2)} * cb * cs`),

  /** 线性加深：cb+cs−1，可能为负，夹到 0。 */
  linearBurn: (d) => ret(`max(${zero(d)}, cb + cs - ${one(d)})`),

  /** 线性减淡（添加）：cb+cs，可能超 1，夹到 1。 */
  linearDodge: (d) => ret(`min(${one(d)}, cb + cs)`),

  /**
   * 亮光：cs≤0.5 时对 2·cs 做 colorBurn，否则对 2·cs−1 做 colorDodge——
   * 跟 hardLight 用 multiply/screen 是同一个"以 cs=0.5 为界二选一"的结构，
   * 只是换成了力道更强的 colorBurn/colorDodge。
   */
  vividLight: (d) => {
    const doubled = `(${formatFloat(2)} * cs)`;
    const burnBranch = colorBurnExpr(d, 'cb', doubled);
    const dodgeBranch = colorDodgeExpr(d, 'cb', `(${doubled} - ${one(d)})`);
    return ret(d.select(dodgeBranch, burnBranch, d.lessEqual('cs', d.splat(0.5))));
  },

  /**
   * 线性光：cs≤0.5 时 linearBurn(cb,2cs)，否则 linearDodge(cb,2cs−1)。
   * 两个分支代数上都化简成同一条 `cb+2cs−1` 再夹到 [0,1]——不用再分支。
   */
  linearLight: (d) =>
    ret(`max(${zero(d)}, min(${one(d)}, cb + ${formatFloat(2)} * cs - ${one(d)}))`),

  /** 点光：cs≤0.5 时 darken(cb,2cs)，否则 lighten(cb,2cs−1)。 */
  pinLight: (d) => {
    const doubled = `(${formatFloat(2)} * cs)`;
    const darkBranch = `min(cb, ${doubled})`;
    const lightBranch = `max(cb, (${doubled} - ${one(d)}))`;
    return ret(d.select(lightBranch, darkBranch, d.lessEqual('cs', d.splat(0.5))));
  },

  /** 实色混合：cb+cs≥1 时纯白，否则纯黑——vividLight 阈值化到 0.5 的代数简化结果。 */
  hardMix: (d) => ret(d.select(zero(d), one(d), d.greaterEqual('(cb + cs)', one(d)))),

  /** 减去：cb−cs，可能为负，夹到 0。 */
  subtract: (d) => ret(`max(${zero(d)}, cb - cs)`),

  /** 划分：cb/cs；cs≤0 时视为"除以无穷小"，统一给白（不看 cb，是常见实现约定）。 */
  divide: (d) => {
    const denom = `max(cs, ${d.splat(EPSILON)})`;
    const divided = `min(${one(d)}, cb / ${denom})`;
    return ret(d.select(divided, one(d), d.lessEqual('cs', zero(d))));
  },
};

/** 混合函数在着色器里的名字。 */
export function blendFunctionName(mode: SeparableBlendMode): string {
  // 驼峰转蛇形：WGSL 与 GLSL 的惯例都是蛇形，且避免与内置名冲突
  return `blend_${mode.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)}`;
}

/* ------------------------------------------------------------------ */
/* CPU 侧参考实现                                                        */
/* ------------------------------------------------------------------ */

/**
 * 与着色器公式同源的 CPU 参考实现，逐条对照 CSS 规范。
 *
 * 它的用途是给 GPU 结果当**独立的对照**：GPU 上跑的是生成出来的源码，
 * 这里跑的是照规范手写的公式，两者一致才说明生成器没有把公式写歪。
 * 若用生成的源码反过来推 CPU 结果，那就成了自己对自己，什么都证明不了。
 */
export const REFERENCE_BLEND: Readonly<Record<SeparableBlendMode, (cb: number, cs: number) => number>> =
  {
    normal: (_cb, cs) => cs,
    multiply: (cb, cs) => cb * cs,
    screen: (cb, cs) => cb + cs - cb * cs,
    overlay: (cb, cs) => REFERENCE_BLEND.hardLight(cs, cb),
    darken: (cb, cs) => Math.min(cb, cs),
    lighten: (cb, cs) => Math.max(cb, cs),
    colorDodge: (cb, cs) => {
      if (cb <= 0) return 0;
      if (cs >= 1) return 1;
      return Math.min(1, cb / (1 - cs));
    },
    colorBurn: (cb, cs) => {
      if (cb >= 1) return 1;
      if (cs <= 0) return 0;
      return 1 - Math.min(1, (1 - cb) / cs);
    },
    hardLight: (cb, cs) =>
      cs <= 0.5 ? REFERENCE_BLEND.multiply(cb, 2 * cs) : REFERENCE_BLEND.screen(cb, 2 * cs - 1),
    softLight: (cb, cs) => {
      const d = cb <= 0.25 ? ((16 * cb - 12) * cb + 4) * cb : Math.sqrt(cb);
      return cs <= 0.5 ? cb - (1 - 2 * cs) * cb * (1 - cb) : cb + (2 * cs - 1) * (d - cb);
    },
    difference: (cb, cs) => Math.abs(cb - cs),
    exclusion: (cb, cs) => cb + cs - 2 * cb * cs,
    // "溶解"的逐通道颜色公式跟 normal 一样，抖动在 composite.ts 里对覆盖度做
    dissolve: (_cb, cs) => cs,
    linearBurn: (cb, cs) => Math.max(0, cb + cs - 1),
    linearDodge: (cb, cs) => Math.min(1, cb + cs),
    vividLight: (cb, cs) =>
      cs <= 0.5 ? REFERENCE_BLEND.colorBurn(cb, 2 * cs) : REFERENCE_BLEND.colorDodge(cb, 2 * cs - 1),
    linearLight: (cb, cs) => Math.max(0, Math.min(1, cb + 2 * cs - 1)),
    pinLight: (cb, cs) => (cs <= 0.5 ? Math.min(cb, 2 * cs) : Math.max(cb, 2 * cs - 1)),
    hardMix: (cb, cs) => (cb + cs >= 1 ? 1 : 0),
    subtract: (cb, cs) => Math.max(0, cb - cs),
    divide: (cb, cs) => (cs <= 0 ? 1 : Math.min(1, cb / cs)),
  };
